/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core";
import axios from "axios";
import {
  KAvatar,
  KIcon,
  KDialog,
  KButton,
} from "@kyburz/kdesign-system";
import useSetState from "../../../hooks/useSetState";
import {
  getDrivers,
  deleteDriver,
  downloadDrivers,
  importDrivers,
  deleteDrivers,
} from "../../../apiCalls/admin/drivers";
import { getOrderByValueDriver } from "../DriverTableFields";
import { setAlert } from "../../../redux/actions/actions";
import BulkUpload from "../../../components/bulkUpload/BulkUpload";
import { driverElements } from "../../../utils/bulkUploadElements";
import KInfiniteTable from "../../../components/table/KInfiniteTable";

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      manualSortBy={true}
      sortTable={props.sortTable}
      initialSortBy={props.initialSortBy}
      onSelectRows={props.onSelectRows}
      autoResetSelectedRows={props.autoResetSelectedRows}
      hasStickyColumns={true}
      hasFilter
      manualFilters
      filterTable={props.filterTable}
      loading={props.loading}
    />
  ),
  (oldProps, newProps) => {
    return (
      oldProps.columns.length === newProps.columns.length &&
      oldProps.data === newProps.data &&
      oldProps.loading === newProps.loading
    );
  },
);

const useStyles = makeStyles(() => ({
  ctaContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 16,
  },
  tableContainer: {
    height: "calc(100% - 100px)",
  },
}));

interface Props {
  ctaText?: string;
  handleAddVehicle?: (v) => void | any;
  handleEditClick?: (v) => void | any;
  handleDeleteClick?: (v, v1) => void | any;
  deletePopupOpen: boolean;
  driverToDelete: string;
  idToDelete: string;
}

const DriversTable: React.FC<Props> = ({
  ctaText = "",
  handleAddVehicle = undefined,
  handleEditClick = undefined,
  handleDeleteClick = undefined,
  deletePopupOpen = false,
  driverToDelete = "",
  idToDelete = "",
}: Props) => {
  const classes = useStyles();
  const { CancelToken } = axios;
  const dispatch = useDispatch();

  const defaultSortingRules = {
    id: "first_name",
    order: "asc",
  };

  const [
    {
      items,
      query,
      cancelTokenSource,
      hasNextPage,
      isNextPageLoading,
      pagesLoaded,
      orderBy,
      order,
      isDownloading,
      isDeleting,
      selectedRows,
      isAllSelected,
      showBulkUploadModal,
      showBulkDeleteModal,
      reload,
      filterBy,
      loading,
    },
    setState,
  ] = useSetState({
    items: [],
    query: "",
    cancelTokenSource: null,
    hasNextPage: true,
    isNextPageLoading: false,
    pagesLoaded: 0,
    orderBy: defaultSortingRules.id,
    order: defaultSortingRules.order,
    isDownloading: false,
    isDeleting: false,
    selectedRows: {},
    isAllSelected: false,
    showBulkUploadModal: false,
    showBulkDeleteModal: false,
    reload: false,
    filterBy: "",
    loading: true,
  });

  const company = useSelector(
    (state: any) => state.customer.currentCustomer?.shortname,
  );

  const fetchDrivers = (
    q: string,
    p: number,
    startEmpty: boolean = true,
    filterBy: string = "",
  ): void => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();
    const sourceToken = source.token;
    setState({
      isNextPageLoading: true,
      cancelTokenSource: source,
      loading: true,
    });
    const previousArray = startEmpty ? [] : items;
    getDrivers(
      company,
      q,
      p,
      sourceToken,
      getOrderByValueDriver(orderBy),
      order,
      filterBy,
    )
      .then((res) => {
        const {
          data: { data },
        } = res;
        setState({
          items: [...previousArray].concat(
            ...data?.map((x) => ({
              id: x._id,
              first_name: x.first_name,
              last_name: x.last_name,
              name: `${x.first_name} ${x.last_name}`,
              category: x.driver_license.categories,
              number: x.phone_number || "",
              email: x.email,
              expiry: x.driver_license.expiry_date || "",
              avatar: x.avatar,
              driverIdentification: x.driver_identification || "",
            })),
          ),
          isNextPageLoading: false,
          pagesLoaded: p,
          loading: false,
        });
      })
      .catch((e) => {
        console.error(e);
        setState({
          isNextPageLoading: false,
          pagesLoaded: p,
        });
      });
  };

  const loadNextPage = () =>
    fetchDrivers(query, pagesLoaded + 1, false, filterBy);

  const sortTable = (data) => {
    const rules = data[0];
    if (rules) {
      setState({
        orderBy: rules.id,
        order: rules.desc ? "desc" : "asc",
      });
    }
  };

  useEffect(() => {
    fetchDrivers(query, 1, true, filterBy);
  }, [query, orderBy, order, filterBy]);

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      fetchDrivers(query, 1, true, filterBy);
    }
  }, [reload]);

  const filterTable = (values: { id: string; value: string }[]): void => {
    const parsedFilter: string = values
      .map((x) => `${x.id}:regex:${x.value}`)
      .join(";");
    setState({ filterBy: parsedFilter });
  };

  const columns = React.useMemo(
    () => [
      {
        disableFilters: true,
        Header: "Avatar",
        accessor: "pic",
        width: 50,
        Cell: (row) => {
          return (
            <div style={{ textAlign: "center" }}>
              <div style={{ marginTop: -4 }}>
                <KAvatar
                  firstName={row.cell.row.original.first_name || ""}
                  lastName={row.cell.row.original.last_name || ""}
                  avatar={row.cell.row.original.avatar}
                  size={28}
                />
              </div>
            </div>
          );
        },
        defaultCanSort: false,
        sortType: (rowA, rowB) => {
          const nameA = rowA.values?.name;
          const nameB = rowB.values?.name;
          return nameA > nameB ? 1 : -1;
        },
      },
      {
        Header: "First Name",
        accessor: "first_name",
        width: 300,
        defaultCanSort: true,
        sortType: (rowA, rowB) => {
          const nameA = rowA.values?.name;
          const nameB = rowB.values?.name;
          return nameA > nameB ? 1 : -1;
        },
      },
      {
        Header: "Last Name",
        accessor: "last_name",
        width: 300,
        Cell: (row) => {
          return (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  marginRight: "16px",
                  cursor: "pointer",
                  float: "left",
                  display: "flex",
                }}
              >
                <div style={{ marginLeft: 24 }}>{row.value}</div>
              </div>
            </div>
          );
        },
        defaultCanSort: true,
        sortType: (rowA, rowB) => {
          const nameA = rowA.values?.name;
          const nameB = rowB.values?.name;
          return nameA > nameB ? 1 : -1;
        },
      },
      {
        Header: "Categories",
        accessor: "category",
        disableFilters: true,
        defaultCanSort: true,
        sortType: (rowA, rowB) => {
          const categoriesA = rowA.values.category.join(", ");
          const categoriesB = rowB.values.category.join(", ");
          return categoriesA > categoriesB ? 1 : -1;
        },
        width: 150,
      },
      {
        Header: "Phone Number",
        accessor: "number",
        defaultCanSort: true,
        disableFilters: true,
      },
      {
        Header: "Email",
        accessor: "email",
        defaultCanSort: true,
        width: 250,
      },
      {
        Header: "Expiry Date",
        accessor: "expiry",
        defaultCanSort: true,
        disableFilters: true,
      },
      {
        Header: "Driver Identification",
        accessor: "driverIdentification",
        defaultCanSort: true,
        width: 200,
        disableFilters: true,
      },
      {
        accessor: "actions",
        disableFilters: true,
        Cell: (row) => {
          return (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  display: "inline-block",
                  marginRight: "16px",
                  cursor: "pointer",
                }}
                onClick={() =>
                  handleEditClick && handleEditClick(row.row.original)
                }
              >
                <KIcon icon="edit" color="#383F51" size={20} />
              </div>
              <div
                style={{ display: "inline-block", cursor: "pointer" }}
                onClick={(e) =>
                  handleDeleteClick && handleDeleteClick(e, row.row.original)
                }
              >
                <KIcon icon="trash-2" color="#383F51" size={20} />
              </div>
            </div>
          );
        },
        width: 100,
        sticky: "right",
      },
    ],
    [],
  );

  const setSelectedRows = (isAllSelected, selectedRows) =>
    setState({
      selectedRows,
      isAllSelected,
    });

  const getSelectedDriverIds = (): string => {
    if (isAllSelected && !filterBy) return "all";
    const driverIds = Object.keys(selectedRows).reduce(
      (res: Array<string>, sr: string) => {
        const id = items[sr]?.id;
        return id ? [...res, id] : res;
      },
      [],
    );
    return driverIds.join(",");
  };

  const exportDrivers = () => {
    setState({ isDownloading: true });
    downloadDrivers(company, getSelectedDriverIds(), query)
      .then((url) => url && window.open(url, "_self"))
      .catch((e) => {
        const errors = e?.response?.data?.errors?.[0];
        const message = errors
          ? Object.values(errors).join("\n")
          : "Couldn't load file";
        dispatch(setAlert(message, "error"));
      })
      .finally(() => setState({ isDownloading: false }));
  };

  const handleDeleteDrivers = () => {
    setState({ isDeleting: true });
    deleteDrivers(company, getSelectedDriverIds(), query)
      .then((res) => {
        setState({
          selectedRows: {},
          showBulkDeleteModal: false,
          reload: true,
        });
        dispatch(
          setAlert(
            `${res.deleted} driver${res.deleted === 1 ? "" : "s"} deleted`,
            "success",
          ),
        );
      })
      .catch((e) => {
        const error = e?.response?.data?.details;
        const message = error || "Couldn't delete drivers";
        dispatch(setAlert(message, "error"));
      })
      .finally(() => setState({ isDeleting: false }));
  };

  const handleDeleteDriver = (e): void => {
    deleteDriver(company, idToDelete).then((res) => {
      if (res?.data) {
        if (fetchDrivers) fetchDrivers("", 1, true);
      } else {
        const message = res || "Error deleting driver";
        dispatch(setAlert(message, "error"));
      }
    });
    if (handleDeleteClick) handleDeleteClick(e, "");
  };

  const deletePopup = (
    <KDialog
      size="xs"
      title="Delete Driver"
      open={deletePopupOpen}
      primaryCTAonClick={(e) => handleDeleteDriver(e)}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={(e) => handleDeleteClick && handleDeleteClick(e, "")}
      secondaryCTAText="Cancel"
    >
      <p>Are you sure you want to delete {driverToDelete} as a driver?</p>
    </KDialog>
  );

  const toggleBulkDeletePopup = () =>
    setState({ showBulkDeleteModal: !showBulkDeleteModal });
  const selectedDriversLength = Object.keys(selectedRows).length;
  const deleteBulkPopup = (
    <KDialog
      size="xs"
      title="Delete Drivers"
      open={showBulkDeleteModal}
      primaryCTAonClick={handleDeleteDrivers}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={() => toggleBulkDeletePopup()}
      secondaryCTAText="Cancel"
    >
      {!query || !isAllSelected ? (
        <p>
          Are you sure you want to delete {selectedDriversLength} driver
          {selectedDriversLength === 1 ? "" : "s"}?
        </p>
      ) : (
        <p>
          Are you sure you want to delete all drivers found by query {query}?
        </p>
      )}
    </KDialog>
  );

  const driverUploadBody = [
    "birth_date",
    "driver_license.categories",
    "driver_identification",
    "tacho_card",
    "first_name",
    "last_name",
    "avatar",
    "email",
    "phone_number",
    "user_id",
    "_id",
  ];
  const toRemove = ["created", "modified"];

  const importBulk = (request): any => {
    return importDrivers(company, request);
  };

  const toggleBulkUpload = () => {
    setState({ showBulkUploadModal: !showBulkUploadModal });
    if (showBulkUploadModal) {
      setState({ reload: true });
    }
  };

  const bulkUploadPopup = (
    <BulkUpload
      open={showBulkUploadModal}
      toggleBulkUpload={toggleBulkUpload}
      elements={driverElements}
      importFunction={importBulk}
      body={driverUploadBody}
      toRemove={toRemove}
    />
  );

  return (
    <>
      <div className={classes.ctaContainer}>
        <KButton
          children="Export XLS"
          icon={<KIcon icon="download-cloud" />}
          onClick={exportDrivers}
          disabled={!Object.keys(selectedRows).length || isDownloading}
          loading={isDownloading}
        />
        <KButton
          children="Import CSV"
          icon={<KIcon icon="upload" />}
          onClick={toggleBulkUpload}
        />
        <KButton
          children={ctaText}
          icon={<KIcon icon="plus" />}
          onClick={handleAddVehicle}
        />
        <KButton
          children="Delete"
          icon={<KIcon icon="trash-2" />}
          onClick={toggleBulkDeletePopup}
          disabled={
            !Object.keys(selectedRows).length ||
            (isAllSelected && !query) ||
            isDeleting
          }
          loading={isDeleting}
        />
      </div>
      <div className={classes.tableContainer}>
        <MemoizedKInfiniteTable
          data={items}
          hasNextPage={hasNextPage}
          isNextPageLoading={isNextPageLoading}
          loadNextPage={loadNextPage}
          columns={columns}
          sortTable={sortTable}
          initialSortBy={[defaultSortingRules]}
          onSelectRows={setSelectedRows}
          autoResetSelectedRows={true}
          filterTable={filterTable}
          loading={loading}
        />
      </div>
      {deletePopup}
      {deleteBulkPopup}
      {bulkUploadPopup}
    </>
  );
};

export default DriversTable;
